import type {
  AlwaysAiUser,
  MyAlertsPreferences,
  MyAlertsResponse,
  NotificationDefaultsConflictResponse,
  NotificationDefaultsRdOption,
  NotificationDefaultsResponse,
  NotificationDefaultsSetting,
} from "../../../shared/api-types.js";
import { can } from "./permissions.js";

// The Notification settings page (NO-834): request helpers over an injected
// transport, who sees which section, plain labels, and the editor's draft
// arithmetic. Pure and relative-import only, so `tsx --test` runs it.

export const NOTIFICATION_SETTINGS_PATH = "/settings/notifications";

const BASE = "/api/notifications/settings";

export type NotificationSettingsRequest = <T>(
  path: string,
  init?: RequestInit
) => Promise<T>;

/** One defaults editor's target: the viewer's own, the practice's, or a picked RD's. */
export type DefaultsTarget =
  | { kind: "mine" }
  | { kind: "practice" }
  | { kind: "rd"; rdId: string };

function targetPath(target: DefaultsTarget): string {
  if (target.kind === "mine") return `${BASE}/my-defaults`;

  if (target.kind === "practice") return `${BASE}/practice`;

  return `${BASE}/rds/${encodeURIComponent(target.rdId)}`;
}

export function defaultsQueryKey(target: DefaultsTarget): readonly string[] {
  return target.kind === "rd"
    ? ["notification-settings", "rd", target.rdId]
    : ["notification-settings", target.kind];
}

export const RD_PICKER_QUERY_KEY = ["notification-settings", "rds"] as const;

export const MY_ALERTS_QUERY_KEY = ["notification-settings", "my-alerts"] as const;

/**
 * The settings reads' query options. A background refetch would replace the
 * values under an unsaved draft, so an open editor changes only through a
 * save's answer. Nothing is cached once the page closes (`gcTime: 0`), so
 * reopening it always waits for a fresh read before the editor shows: it
 * never starts from values cached before someone else saved, and no late
 * read can land on top of edits already made.
 */
export const SETTINGS_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

export interface DefaultsChange {
  kind: string;
  channel: string;
  enabled: boolean | null;
  offsetsMinutes: number[] | null;
  expectedVersion: number;
}

export function createNotificationSettingsClient(request: NotificationSettingsRequest) {
  return {
    async read(target: DefaultsTarget): Promise<NotificationDefaultsResponse> {
      return (await request<{ data: NotificationDefaultsResponse }>(targetPath(target)))
        .data;
    },
    async save(
      target: DefaultsTarget,
      changes: DefaultsChange[]
    ): Promise<NotificationDefaultsResponse> {
      const init: RequestInit = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ changes }),
      };

      return (
        await request<{ data: NotificationDefaultsResponse }>(targetPath(target), init)
      ).data;
    },
    async readMyAlerts(): Promise<MyAlertsResponse> {
      return (await request<{ data: MyAlertsResponse }>(`${BASE}/my-alerts`)).data;
    },
    async saveMyAlerts(save: MyAlertsSave): Promise<MyAlertsResponse> {
      const init: RequestInit = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(save),
      };

      return (await request<{ data: MyAlertsResponse }>(`${BASE}/my-alerts`, init))
        .data;
    },
    async listRds(): Promise<NotificationDefaultsRdOption[]> {
      return (await request<{ data: NotificationDefaultsRdOption[] }>(`${BASE}/rds`))
        .data;
    },
  };
}

export type NotificationSettingsClient = ReturnType<
  typeof createNotificationSettingsClient
>;

// --- Who sees what -----------------------------------------------------------

/** Their own sections need their own appointments; v1 alerts are about those. */
export function canEditOwnNotificationSettings(user: AlwaysAiUser): boolean {
  return can(user, "appointments:read:own");
}

/** The Practice defaults section, with its RD picker (NO-827's permission). */
export function canManagePracticeDefaults(user: AlwaysAiUser): boolean {
  return can(user, "notification_defaults:manage:all");
}

/** Does the account menu offer Notifications? Flag on, and a section to show. */
export function hasNotificationSettings(
  user: AlwaysAiUser,
  notificationsEnabled: boolean
): boolean {
  if (!notificationsEnabled) return false;

  return canEditOwnNotificationSettings(user) || canManagePracticeDefaults(user);
}

/** The page is the menu item's destination, in the single-rail frame only. */
export function notificationSettingsPageState(input: {
  frame: "rail" | "classic" | null;
  featuresLoading: boolean;
  notificationsEnabled: boolean;
  user: AlwaysAiUser | undefined;
}): "blank" | "not_found" | "page" {
  if (input.frame !== "rail") return "not_found";

  if (input.featuresLoading) return "blank";

  const shown =
    input.user !== undefined &&
    hasNotificationSettings(input.user, input.notificationsEnabled);

  return shown ? "page" : "not_found";
}

// --- Labels ------------------------------------------------------------------

/** The Notification Kinds an editor shows, in this order, in plain words. */
export const MESSAGE_TYPES = [
  {
    kind: "appointment-reminder",
    label: "Appointment reminder",
    description: "Sent before each visit.",
  },
  {
    kind: "appointment-scheduled-by-rd",
    label: "Appointment booked by the RD",
    description: "Sent when the RD or the care team books a visit for the Client.",
  },
  {
    kind: "appointment-booked-by-client",
    label: "Appointment booked by the Client",
    description: "Sent when the Client books a visit themselves.",
  },
  {
    kind: "appointment-updated",
    label: "Appointment moved",
    description: "Sent when a visit's date or time changes.",
  },
  {
    kind: "appointment-cancelled",
    label: "Appointment cancelled",
    description: "Sent when a visit is cancelled.",
  },
] as const;

/** Only email sends today; SMS rows stay in the API for when it arrives. */
export const EDITED_CHANNEL = "email";

export function reminderTimeLabel(minutes: number): string {
  if (minutes < 1440) {
    const hours = Math.round(minutes / 60);

    return hours === 1 ? "1 hour before" : `${hours} hours before`;
  }

  const days = Math.round(minutes / 1440);

  return days === 1 ? "1 day before" : `${days} days before`;
}

export function messageTypeLabel(kind: string): string {
  return MESSAGE_TYPES.find((type) => type.kind === kind)?.label ?? kind;
}

// --- The editor's draft ------------------------------------------------------

export type EditorLayer = "rd" | "practice";

/** One row's editable values: this layer's own, null meaning "not set here". */
export interface DraftRow {
  enabled: boolean | null;
  offsetsMinutes: number[] | null;
}

export type DefaultsDraft = Record<string, DraftRow>;

export function rowKey(
  row: Pick<NotificationDefaultsSetting, "kind" | "channel">
): string {
  return `${row.kind}|${row.channel}`;
}

/** The rows the editor shows: email only, in MESSAGE_TYPES order. */
export function editorRows(
  view: NotificationDefaultsResponse
): NotificationDefaultsSetting[] {
  return MESSAGE_TYPES.flatMap((type) =>
    view.settings.filter(
      (row) => row.kind === type.kind && row.channel === EDITED_CHANNEL
    )
  );
}

/**
 * Where a row starts. The practice layer always answers, so an unset practice
 * row starts off, and its reminder starts with no times chosen.
 */
function startingRow(row: NotificationDefaultsSetting, layer: EditorLayer): DraftRow {
  if (layer === "rd") return { ...row.value };

  return {
    enabled: row.value.enabled ?? false,
    offsetsMinutes: row.timed ? (row.value.offsetsMinutes ?? []) : null,
  };
}

export function startingDraft(
  view: NotificationDefaultsResponse,
  layer: EditorLayer
): DefaultsDraft {
  return Object.fromEntries(
    editorRows(view).map((row) => [rowKey(row), startingRow(row, layer)])
  );
}

function inheritedTimes(row: NotificationDefaultsSetting): number[] {
  return row.inherited.offsetsMinutes ?? [];
}

/** What the Client gets from this row as drafted: this layer's value, else the one behind it. */
export function shownRow(
  row: NotificationDefaultsSetting,
  draft: DraftRow
): { enabled: boolean; offsetsMinutes: number[] } {
  return {
    enabled: draft.enabled ?? row.inherited.enabled,
    offsetsMinutes: draft.offsetsMinutes ?? inheritedTimes(row),
  };
}

/** An RD row that sets nothing of its own follows the practice default. */
export function followsPractice(draft: DraftRow): boolean {
  return draft.enabled === null && draft.offsetsMinutes === null;
}

export function withEnabled(draft: DraftRow, enabled: boolean): DraftRow {
  return { ...draft, enabled };
}

/** Adds or removes one reminder time, starting from what the row shows now. */
export function withReminderTime(
  draft: DraftRow,
  shown: number[],
  minutes: number,
  checked: boolean
): DraftRow {
  const others = shown.filter((value) => value !== minutes);
  const next = checked ? [...others, minutes] : others;

  return { ...draft, offsetsMinutes: next.sort((left, right) => right - left) };
}

export const FOLLOW_PRACTICE: DraftRow = { enabled: null, offsetsMinutes: null };

function sameRow(left: DraftRow, right: DraftRow): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * No times chosen sends as "not set here": NO-829 refuses an empty list, and
 * an RD row that is off with no times of its own falls back to the practice's.
 * The practice editor never gets here with none (`draftProblem`).
 */
function sentTimes(offsets: number[] | null): number[] | null {
  return offsets !== null && offsets.length === 0 ? null : offsets;
}

/** Every row the editor changed, each naming the version it was read at. */
export function draftChanges(
  view: NotificationDefaultsResponse,
  layer: EditorLayer,
  draft: DefaultsDraft
): DefaultsChange[] {
  return editorRows(view).flatMap((row) => {
    const drafted = draft[rowKey(row)];

    if (!drafted || sameRow(drafted, startingRow(row, layer))) return [];

    return [
      {
        kind: row.kind,
        channel: row.channel,
        enabled: drafted.enabled,
        offsetsMinutes: row.timed ? sentTimes(drafted.offsetsMinutes) : null,
        expectedVersion: row.version,
      },
    ];
  });
}

/** A reminder that would send with no time chosen; null when the draft is valid. */
export function draftProblem(
  view: NotificationDefaultsResponse,
  layer: EditorLayer,
  draft: DefaultsDraft
): string | null {
  const missingTime = editorRows(view).some((row) => {
    const drafted = draft[rowKey(row)];
    if (!row.timed || !drafted) return false;

    const shown = shownRow(row, drafted);
    const noTime = shown.offsetsMinutes.length === 0;

    // The practice layer always carries reminder times, on or off (NO-829).
    return layer === "practice" ? noTime : shown.enabled && noTime;
  });

  return missingTime ? "Choose at least one reminder time." : null;
}

/**
 * Rows someone else saved while this editor had the page open: a row this
 * save did not send whose version rose. The page then shows their values.
 */
export function changedByOthers(
  opened: NotificationDefaultsResponse,
  saved: NotificationDefaultsResponse,
  sent: readonly DefaultsChange[]
): string[] {
  const sentKeys = new Set(sent.map(rowKey));
  const openedVersions = new Map(
    opened.settings.map((row) => [rowKey(row), row.version])
  );

  return editorRows(saved)
    .filter((row) => !sentKeys.has(rowKey(row)))
    .filter((row) => row.version > (openedVersions.get(rowKey(row)) ?? 0))
    .map((row) => messageTypeLabel(row.kind));
}

interface ConflictCarrier {
  status?: unknown;
  body?: unknown;
}

/** The current settings a 409 carried, or null when the error is something else. */
export function conflictCurrent(error: unknown): NotificationDefaultsResponse | null {
  const { status, body } = (error ?? {}) as ConflictCarrier;
  if (status !== 409 || typeof body !== "object" || body === null) return null;

  const conflict = body as Partial<NotificationDefaultsConflictResponse>;

  return conflict.code === "version_conflict" && conflict.current
    ? conflict.current
    : null;
}

/** The own-defaults read answers 404 `no_rd_profile` for an RD with no linked Provider. */
export function isMissingRdProfile(error: unknown): boolean {
  const { status, body } = (error ?? {}) as ConflictCarrier;

  return (
    status === 404 && (body as { code?: unknown } | null)?.code === "no_rd_profile"
  );
}

// --- My alerts ---------------------------------------------------------------

/** The alert preferences the page edits: all but the lead time. */
export type MyAlertsDraft = Omit<MyAlertsPreferences, "reminderLeadMinutes">;

export interface MyAlertsSave {
  expectedVersion: number;
  changes: Partial<MyAlertsDraft>;
}

const MY_ALERT_FIELDS = [
  "reminderEnabled",
  "bookedEnabled",
  "cancelledEnabled",
  "rescheduledEnabled",
  "agendaEnabled",
  "agendaMinuteOfDay",
  "agendaTimeZone",
] as const satisfies readonly (keyof MyAlertsDraft)[];

/** The on/off alerts, in the order the page lists them. */
export const MY_ALERT_SWITCHES = [
  { field: "reminderEnabled", label: "Before each visit" },
  { field: "bookedEnabled", label: "When a Client books a visit" },
  { field: "rescheduledEnabled", label: "When a Client moves a visit" },
  { field: "cancelledEnabled", label: "When a Client cancels a visit" },
] as const satisfies readonly { field: keyof MyAlertsDraft; label: string }[];

export function myAlertsDraft(view: MyAlertsResponse): MyAlertsDraft {
  return Object.fromEntries(
    MY_ALERT_FIELDS.map((field) => [field, view.preferences[field]])
  ) as MyAlertsDraft;
}

/** "07:00" for 420; "" when no time is set. */
export function minuteToTime(minute: number | null): string {
  if (minute === null) return "";

  const hours = String(Math.floor(minute / 60)).padStart(2, "0");

  return `${hours}:${String(minute % 60).padStart(2, "0")}`;
}

/** 420 for "07:00"; null for anything that is not a time of day. */
export function timeToMinute(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);

  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

/**
 * Turning the agenda on, or changing its time, saves the agenda in the zone
 * the RD is in now; that is the zone the time is shown in.
 */
export function withAgendaTime(
  draft: MyAlertsDraft,
  minute: number | null,
  zone: string
): MyAlertsDraft {
  return { ...draft, agendaMinuteOfDay: minute, agendaTimeZone: zone };
}

export function withAgendaEnabled(
  draft: MyAlertsDraft,
  enabled: boolean,
  zone: string
): MyAlertsDraft {
  const zoned = enabled ? { agendaTimeZone: draft.agendaTimeZone ?? zone } : {};

  return { ...draft, ...zoned, agendaEnabled: enabled };
}

/** The changed fields, at the version the page read. */
export function myAlertsSave(
  view: MyAlertsResponse,
  draft: MyAlertsDraft
): MyAlertsSave {
  const opened = myAlertsDraft(view);
  const changes = Object.fromEntries(
    MY_ALERT_FIELDS.filter((field) => draft[field] !== opened[field]).map((field) => [
      field,
      draft[field],
    ])
  ) as Partial<MyAlertsDraft>;

  return { expectedVersion: view.version, changes };
}

/** An agenda turned on needs a time; null when the draft can save. */
export function myAlertsProblem(draft: MyAlertsDraft): string | null {
  return draft.agendaEnabled && draft.agendaMinuteOfDay === null
    ? "Choose a time for your daily agenda."
    : null;
}

/** The current alerts a 409 carried, or null when the error is something else. */
export function myAlertsConflictCurrent(error: unknown): MyAlertsResponse | null {
  const { status, body } = (error ?? {}) as ConflictCarrier;
  if (status !== 409 || typeof body !== "object" || body === null) return null;

  const conflict = body as { code?: unknown; current?: MyAlertsResponse };

  return conflict.code === "version_conflict" && conflict.current
    ? conflict.current
    : null;
}
